import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControlLabel, Grid, MenuItem, Switch, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { SetupChargeApiError, SetupChargeService } from "../../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupChargeConfig, ISetupChargePartner, TSetupChargeFeePayer, TSetupChargePaymentMethod } from "../../../../shared/services/api/setupCharge/Types";
import { setupChargeFormatCurrency, setupChargeFormatDate, setupChargeToNumber } from "../../../finan/utils/setupChargeFormat";

interface SetupChargeConfigCardProps {
	clientId: string;
	/** Quem pode alterar (perfil Garantti com permissão de edição de clientes). */
	canEdit: boolean;
}

const GATEWAYS = ["ASAAS"];
const PAYMENT_METHODS: { value: TSetupChargePaymentMethod; label: string }[] = [
	{ value: "PIX", label: "PIX" },
	{ value: "BOLETO", label: "Boleto" },
	{ value: "CREDIT_CARD", label: "Cartão de crédito" },
];
const FEE_PAYERS: { value: TSetupChargeFeePayer; label: string }[] = [
	{ value: "payer", label: "Pagador" },
	{ value: "client", label: "Imobiliária" },
	{ value: "garantti", label: "Garantti" },
];

const parseMoney = (text: string): number | undefined => setupChargeToNumber(text.trim().replace(/\./g, "").replace(",", "."));
const toMoneyText = (value?: number): string => (value === undefined ? "" : value.toFixed(2).replace(".", ","));

/**
 * Parâmetros da Taxa Setup Avulsa da imobiliária. Fica separado do formulário do cadastro (tem o próprio botão
 * de salvar) e não altera nada da configuração atual de taxa de setup vinculada.
 */
export const SetupChargeConfigCard = ({ clientId, canEdit }: SetupChargeConfigCardProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const [enabled, setEnabled] = useState(false);
	const [ruleType, setRuleType] = useState<"list" | "range">("range");
	const [values, setValues] = useState<number[]>([]);
	const [valueDraft, setValueDraft] = useState("");
	const [min, setMin] = useState("");
	const [max, setMax] = useState("");
	const [gateway, setGateway] = useState("ASAAS");
	const [paymentMethods, setPaymentMethods] = useState<TSetupChargePaymentMethod[]>([]);
	const [linkValidityDays, setLinkValidityDays] = useState("7");
	const [repassPercent, setRepassPercent] = useState("70");
	const [maxInstallments, setMaxInstallments] = useState("1");
	const [feePayer, setFeePayer] = useState<TSetupChargeFeePayer>("payer");
	const [partner, setPartner] = useState("");
	const [partners, setPartners] = useState<ISetupChargePartner[]>([]);
	const [updatedAt, setUpdatedAt] = useState<string | null>(null);

	const applyConfig = (config: ISetupChargeConfig) => {
		setEnabled(!!config.enabled);
		setRuleType(config.valueRule?.type ?? "range");
		setValues(config.valueRule?.values ?? []);
		setMin(toMoneyText(config.valueRule?.min));
		setMax(toMoneyText(config.valueRule?.max));
		setGateway(config.gateway ?? "ASAAS");
		setPaymentMethods(config.paymentMethods ?? []);
		setLinkValidityDays(String(config.linkValidityDays ?? 7));
		setRepassPercent(String(config.repassPercent ?? 70));
		setMaxInstallments(String(config.maxInstallments ?? 1));
		setFeePayer(config.feePayer ?? "payer");
		setPartner(config.partner ?? "");
		setUpdatedAt(config.updatedAt ?? null);
	};

	const load = useCallback(async () => {
		setIsLoading(true);
		const result = await SetupChargeService.getConfig(clientId);
		setIsLoading(false);
		if (result instanceof Error) {
			setLoadError(result.message);
			return;
		}
		setLoadError(null);
		applyConfig(result);

		// a lista de parceiros só alimenta o seletor: se falhar, o campo continua com o valor já salvo
		const partnerList = await SetupChargeService.listPartners();
		setPartners(partnerList instanceof Error ? [] : partnerList);
	}, [clientId]);

	useEffect(() => {
		load();
	}, [load]);

	const markDirty = () => setSaved(false);

	const handleAddValue = () => {
		const parsed = parseMoney(valueDraft);
		if (parsed === undefined || parsed <= 0) {
			setFieldErrors({ ...fieldErrors, "valueRule.values": "Informe um valor maior que zero." });
			return;
		}
		setFieldErrors({ ...fieldErrors, "valueRule.values": "" });
		setValues((current) => Array.from(new Set([...current, Math.round(parsed * 100) / 100])).sort((a, b) => a - b));
		setValueDraft("");
		markDirty();
	};

	const handleSave = async () => {
		setSaveError(null);
		setSaved(false);

		const payload: Partial<ISetupChargeConfig> = {
			enabled,
			gateway,
			paymentMethods: paymentMethods.length > 0 ? paymentMethods : null,
			linkValidityDays: Number(linkValidityDays),
			repassPercent: Number(repassPercent.replace(",", ".")),
			maxInstallments: Number(maxInstallments),
			feePayer,
			partner: partner || null,
			valueRule: ruleType === "list" ? { type: "list", values } : { type: "range", min: parseMoney(min), max: parseMoney(max) },
		};

		setIsSaving(true);
		const result = await SetupChargeService.saveConfig(clientId, payload);
		setIsSaving(false);

		if (result instanceof Error) {
			const error = result as SetupChargeApiError;
			const byField: Record<string, string> = {};
			error.details.forEach((detail) => {
				if (detail.field) byField[detail.field] = detail.message ?? detail.reason ?? "";
			});
			setFieldErrors(byField);
			setSaveError(error.message);
			return;
		}

		setFieldErrors({});
		applyConfig(result);
		setSaved(true);
	};

	const disabled = !canEdit || isSaving;

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent>
				<Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
					<Box>
						<Typography variant="h6" fontWeight={400} color="secondary">
							Taxa Setup Avulsa
						</Typography>
						{updatedAt && (
							<Typography variant="caption" color="textSecondary">
								Última alteração em {setupChargeFormatDate(updatedAt, true)}
							</Typography>
						)}
					</Box>
					<FormControlLabel
						control={
							<Switch
								checked={enabled}
								onChange={(e) => {
									setEnabled(e.target.checked);
									markDirty();
								}}
								disabled={disabled || isLoading}
							/>
						}
						label="Habilitar avulsa"
					/>
				</Box>

				{loadError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{loadError}
					</Alert>
				)}
				{saveError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{saveError}
					</Alert>
				)}
				{saved && (
					<Alert severity="success" sx={{ mb: 2 }}>
						Configuração da taxa avulsa salva.
					</Alert>
				)}

				<Grid container spacing={2}>
					<Grid item xs={12} sm={4} md={3}>
						<TextField
							select
							fullWidth
							size="small"
							label="Regra de valores"
							value={ruleType}
							onChange={(e) => {
								setRuleType(e.target.value as "list" | "range");
								markDirty();
							}}
							disabled={disabled}
							error={!!fieldErrors["valueRule.type"]}
							helperText={fieldErrors["valueRule.type"]}
						>
							<MenuItem value="range">Faixa (mínimo e máximo)</MenuItem>
							<MenuItem value="list">Lista de valores</MenuItem>
						</TextField>
					</Grid>

					{ruleType === "range" ? (
						<>
							<Grid item xs={6} sm={4} md={2}>
								<TextField
									fullWidth
									size="small"
									label="Valor mínimo (R$)"
									value={min}
									onChange={(e) => {
										setMin(e.target.value);
										markDirty();
									}}
									disabled={disabled}
									error={!!fieldErrors["valueRule.min"]}
									helperText={fieldErrors["valueRule.min"]}
								/>
							</Grid>
							<Grid item xs={6} sm={4} md={2}>
								<TextField
									fullWidth
									size="small"
									label="Valor máximo (R$)"
									value={max}
									onChange={(e) => {
										setMax(e.target.value);
										markDirty();
									}}
									disabled={disabled}
									error={!!fieldErrors["valueRule.max"]}
									helperText={fieldErrors["valueRule.max"]}
								/>
							</Grid>
						</>
					) : (
						<Grid item xs={12} sm={8} md={7}>
							<Box display="flex" gap={1}>
								<TextField
									size="small"
									label="Adicionar valor (R$)"
									value={valueDraft}
									onChange={(e) => setValueDraft(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											handleAddValue();
										}
									}}
									disabled={disabled}
									error={!!fieldErrors["valueRule.values"]}
									helperText={fieldErrors["valueRule.values"]}
								/>
								<Button variant="outlined" onClick={handleAddValue} disabled={disabled}>
									Adicionar
								</Button>
							</Box>
							<Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
								{values.map((value) => (
									<Chip
										key={value}
										label={setupChargeFormatCurrency(value)}
										onDelete={
											disabled
												? undefined
												: () => {
														setValues((current) => current.filter((item) => item !== value));
														markDirty();
												  }
										}
									/>
								))}
							</Box>
						</Grid>
					)}
					{fieldErrors.valueRule && (
						<Grid item xs={12}>
							<Typography variant="caption" color="error">
								{fieldErrors.valueRule}
							</Typography>
						</Grid>
					)}

					<Grid item xs={6} sm={4} md={3}>
						<TextField
							select
							fullWidth
							size="small"
							label="Gateway"
							value={gateway}
							onChange={(e) => {
								setGateway(e.target.value);
								markDirty();
							}}
							disabled={disabled}
							error={!!fieldErrors.gateway}
							helperText={fieldErrors.gateway}
						>
							{GATEWAYS.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={6} sm={4} md={3}>
						<TextField
							select
							fullWidth
							size="small"
							label="Formas de pagamento"
							InputLabelProps={{ shrink: true }}
							value={paymentMethods}
							onChange={(e) => {
								setPaymentMethods(typeof e.target.value === "string" ? (e.target.value.split(",") as TSetupChargePaymentMethod[]) : (e.target.value as TSetupChargePaymentMethod[]));
								markDirty();
							}}
							SelectProps={{
								multiple: true,
								displayEmpty: true,
								renderValue: (selected) => ((selected as string[]).length === 0 ? "Todas as do gateway" : (selected as string[]).map((s) => PAYMENT_METHODS.find((m) => m.value === s)?.label ?? s).join(", ")),
							}}
							disabled={disabled}
							error={!!fieldErrors.paymentMethods}
							helperText={fieldErrors.paymentMethods}
						>
							{PAYMENT_METHODS.map((method) => (
								<MenuItem key={method.value} value={method.value}>
									{method.label}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={6} sm={4} md={2}>
						<TextField
							fullWidth
							size="small"
							type="number"
							label="Validade do link (dias)"
							value={linkValidityDays}
							onChange={(e) => {
								setLinkValidityDays(e.target.value);
								markDirty();
							}}
							disabled={disabled}
							inputProps={{ min: 1, max: 90 }}
							error={!!fieldErrors.linkValidityDays}
							helperText={fieldErrors.linkValidityDays}
						/>
					</Grid>
					<Grid item xs={6} sm={4} md={2}>
						<TextField
							fullWidth
							size="small"
							label="% de repasse"
							value={repassPercent}
							onChange={(e) => {
								setRepassPercent(e.target.value);
								markDirty();
							}}
							disabled={disabled}
							error={!!fieldErrors.repassPercent}
							helperText={fieldErrors.repassPercent}
						/>
					</Grid>
					<Grid item xs={6} sm={4} md={2}>
						<TextField
							fullWidth
							size="small"
							type="number"
							label="Máx. de parcelas (cartão)"
							value={maxInstallments}
							onChange={(e) => {
								setMaxInstallments(e.target.value);
								markDirty();
							}}
							disabled={disabled}
							inputProps={{ min: 1, max: 12 }}
							error={!!fieldErrors.maxInstallments}
							helperText={fieldErrors.maxInstallments}
						/>
					</Grid>
					<Grid item xs={6} sm={4} md={3}>
						<TextField
							select
							fullWidth
							size="small"
							label="Quem arca com a taxa do gateway"
							value={feePayer}
							onChange={(e) => {
								setFeePayer(e.target.value as TSetupChargeFeePayer);
								markDirty();
							}}
							disabled={disabled}
							error={!!fieldErrors.feePayer}
							helperText={fieldErrors.feePayer}
						>
							{FEE_PAYERS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<TextField
							select
							fullWidth
							size="small"
							label="Parceiro exclusivo"
							value={partner}
							onChange={(e) => {
								setPartner(e.target.value);
								markDirty();
							}}
							disabled={disabled}
							InputLabelProps={{ shrink: true }}
							SelectProps={{ displayEmpty: true }}
							error={!!fieldErrors.partner}
							helperText={fieldErrors.partner}
						>
							<MenuItem value="">Qualquer parceiro</MenuItem>
							{partner && !partners.some((item) => item.partner === partner) && <MenuItem value={partner}>{partner}</MenuItem>}
							{partners.map((item) => (
								<MenuItem key={item.partner} value={item.partner}>
									{item.name ?? item.partner}
								</MenuItem>
							))}
						</TextField>
					</Grid>
				</Grid>

				{canEdit && (
					<Box display="flex" justifyContent="flex-end" mt={2}>
						<Button variant="contained" disableElevation onClick={handleSave} disabled={isSaving || isLoading} startIcon={isSaving ? <CircularProgress size={16} /> : undefined}>
							Salvar configuração da avulsa
						</Button>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};
